/**
 * The plan set's manufacturer documents as the Permit workspace shows them (the preview's `inputs.documents`,
 * app/design/permits/documents.py `manifest`): what state each is in, how to view it (a short-lived link signed per
 * click: `GET /v1/orgs/{org}/permit-datasheets/link`), and which ones the company may replace with its own PDF.
 */
export interface ManifestEntry {
  role: string;
  label: string;
  subject_kind: string;
  subject_key: string;
  doc_kind: string;
  required: boolean;
  source: string | null;
  origin?: string | null;
  file_id: string | null;
  pages: number;
  official_url?: string | null;
  pending_fetch?: boolean;
  attach?: "whole" | "pages" | "reference";
  page_range?: [number, number];
  total_pages?: number;
  revision_id?: string;
  title?: string;
  version?: string | null;
}

export type DatasheetState =
  | { kind: "attached"; pages: number; part?: { first: number; last: number; total: number } }
  | { kind: "fetch" }
  | { kind: "reference"; total?: number }
  | { kind: "generic" }
  | { kind: "missing" };

export function datasheetState(e: ManifestEntry): DatasheetState {
  if (e.file_id) {
    const part = e.attach === "pages" && e.page_range && e.total_pages ? { first: e.page_range[0], last: e.page_range[1], total: e.total_pages } : undefined;
    return { kind: "attached", pages: e.pages, ...(part ? { part } : {}) };
  }
  if (e.pending_fetch) return { kind: "fetch" };
  if (e.attach === "reference") return { kind: "reference", total: e.total_pages };
  if (e.label.startsWith("Generic")) return { kind: "generic" };
  return { kind: "missing" };
}

/** What the link endpoint needs to sign a view of the document, or null when there is nothing stored to view. */
export function viewTarget(e: ManifestEntry): { source: "library" | "document" | "equipment"; id: string } | null {
  if (e.source === "library") {
    const id = e.file_id ?? e.revision_id;
    return id ? { source: "library", id } : null;
  }
  if (!e.file_id) return null;
  if (e.source === "equipment") return { source: "equipment", id: e.file_id };
  if (e.source === "document") return { source: "document", id: e.file_id };
  return null;
}

/** The company's own PDF (it can be removed); everything else can be replaced by one. Generic placeholders take none. */
export function ownDocumentId(e: ManifestEntry): string | null {
  return e.origin === "company" && e.source === "document" && e.file_id ? e.file_id : null;
}

export function canAttachOwn(e: ManifestEntry): boolean {
  return e.doc_kind === "datasheet" && !e.label.startsWith("Generic") && !e.subject_key.startsWith("generic");
}

/** The datasheets (Equipment step list) and the rail's supporting documents (span tables, certification letters). */
export function splitManifest(entries: ManifestEntry[]): { datasheets: ManifestEntry[]; supporting: ManifestEntry[] } {
  return {
    datasheets: entries.filter((e) => e.doc_kind === "datasheet"),
    supporting: entries.filter((e) => e.doc_kind === "span_table" || e.doc_kind === "cert_letter"),
  };
}

/** Whether a datasheet the building department needs is missing (the `documents_datasheets` problem). */
export function missingCount(entries: ManifestEntry[]): number {
  return entries.filter((e) => e.doc_kind === "datasheet" && e.required && !e.file_id && !e.pending_fetch).length;
}

/** A PDF the company may upload (the server checks it again: readable, at most 20 MB and 30 pages). */
export function pdfProblem(file: { name: string; type: string; size: number }): "notPdf" | "tooLarge" | null {
  if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) return "notPdf";
  if (file.size > 20 * 1024 * 1024) return "tooLarge";
  return null;
}
